import { format, parse, differenceInCalendarDays } from 'date-fns';
import { EnumUtils } from './../../../utils';
import { RouteLocation, RouteLocationRaw } from 'vue-router';
import { FlightCore } from './flight';
import { FlightsWidgets } from './widgets';
import { FlightsUseCases } from '../../../use-case';

export namespace Flight {
  export interface History {
    records: HistoryRecord[];
    version: number;
  }

  export interface HistoryRecord {
    from: string;
    to: string;
    price?: string;
    criteria: SearchCriteria;
    priceUpdateAt?: number;
    updatedAt: number;
  }

  export enum RequestTypes {
    Search,
    BrowsePrice,
  }

  export class SearchCriteria {
    tripType: FlightCore.TripType;
    flightFromId: string;
    flightToId: string;
    departDate: Date;
    returnDate: Date;
    cabinClass: FlightCore.CabinClass;
    adults: number;
    stops: number;
    sortBy?: FlightCore.SortBy;
    path?: string;
    childrenAges: number[];

    constructor({
      tripType,
      flightFromId,
      flightToId,
      departDate,
      returnDate,
      cabinClass,
      adults,
      childrenAges,
    }: {
      tripType?: FlightCore.TripType;
      flightFromId?: string;
      flightToId?: string;
      departDate?: Date;
      returnDate?: Date;
      cabinClass?: FlightCore.CabinClass;
      adults?: number;
      childrenAges?: number[];
    }) {
      this.tripType = tripType;
      this.flightFromId = flightFromId;
      this.flightToId = flightToId;
      this.departDate = departDate;
      this.returnDate = returnDate;
      this.cabinClass = cabinClass;
      this.adults = adults;
      this.childrenAges = childrenAges ?? [];
    }

    static empty() {
      const today = new Date();
      const departDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const returnDate = new Date(today.getTime() + 48 * 60 * 60 * 1000);

      return new SearchCriteria({
        tripType: FlightCore.TripType.RoundTrip,
        departDate,
        returnDate,
        cabinClass: FlightCore.CabinClass.Economy,
        adults: 1,
        childrenAges: [],
      });
    }

    static fromPersistance(record: HistoryRecord) {
      return new SearchCriteria({
        flightFromId: record.criteria.flightFromId,
        flightToId: record.criteria.flightToId,
        departDate: parse(record.criteria.departDate),
        returnDate: record.criteria.returnDate
          ? parse(record.criteria.returnDate)
          : null,
        adults: record.criteria.adults,
        tripType: record.criteria.tripType,
        cabinClass: record.criteria.cabinClass,
        childrenAges: record.criteria.childrenAges,
      });
    }

    fromNearbyDatesWidget(date: FlightsWidgets.DateCell): SearchCriteria {
      return new SearchCriteria({
        flightFromId: this.flightFromId,
        flightToId: this.flightToId,
        departDate: parse(date.departureDate),
        returnDate: date.returnDate ? parse(date.returnDate) : null,
        adults: this.adults,
        tripType: this.tripType,
        cabinClass: this.cabinClass,
        childrenAges: this.childrenAges,
      });
    }

    isFlightFromValid(): boolean {
      return !!this.flightFromId;
    }

    isFlightToValid(): boolean {
      return !!this.flightToId;
    }

    toLocationRaw(): RouteLocationRaw {
      const id = FlightsUseCases.composeSearchID({
        fromId: this.flightFromId,
        toId: this.flightToId,
        tripType: this.tripType,
        departDate: this.departDate,
        returnDate: this.returnDate,
      });

      const query = {
        c: this.cabinClass,
        a: this.adults,
      };

      const childrenAges = this.childrenAges.filter(
        it => typeof it === 'number',
      );

      if (childrenAges.length > 0) {
        query['ch'] = childrenAges.join(',');
      }

      return { path: `/flights/search/${id}/`, query: query };
    }

    isChildrenAgesValid(): boolean {
      const infants = this.childrenAges.filter(
        it => typeof it === 'number' && it < 2,
      ).length;
      return this.adults >= infants;
    }

    generateSkyscannerRedirectLink(): string {
      const YYMMdd = 'YYMMDD';
      let dates = [];

      dates.push(format(this.departDate, YYMMdd));

      if (this.tripType === FlightCore.TripType.RoundTrip && this.returnDate) {
        dates.push(format(this.returnDate, YYMMdd));
      }

      let link = `https://www.skyscanner.net/transport/flights/${
        this.flightFromId
      }/${this.flightToId}/${dates.join('/')}/?adults=${
        this.adults
      }&cabinclass=${this.cabinClass}`;

      const childrenAges = this.childrenAges.filter(
        it => typeof it === 'number',
      );

      if (childrenAges.length > 0) {
        link += `&childrenv2=${childrenAges.join('|')}`;
      }

      return link;
    }

    toBody<T extends SearchCriteriaRequest | IndicativeSearchRequest>(
      type: RequestTypes,
    ): T {
      const dateWrapper = (date: Date): DateModel => {
        return {
          day: date.getDate(),
          month: date.getMonth() + 1,
          year: date.getFullYear(),
        };
      };

      if (type === RequestTypes.Search) {
        const queryLegs: QueryLeg[] = [];

        queryLegs.push({
          originPlaceId: {
            iata: this.flightFromId,
          },
          destinationPlaceId: {
            iata: this.flightToId,
          },
          date: dateWrapper(this.departDate),
        });

        if (
          this.tripType === FlightCore.TripType.RoundTrip &&
          this.returnDate
        ) {
          queryLegs.push({
            originPlaceId: {
              iata: this.flightToId,
            },
            destinationPlaceId: {
              iata: this.flightFromId,
            },
            date: dateWrapper(this.returnDate),
          });
        }

        const cabinClassMap = {
          [FlightCore.CabinClass.Economy]: 'CABIN_CLASS_ECONOMY',
          [FlightCore.CabinClass.Business]: 'CABIN_CLASS_BUSINESS',
          [FlightCore.CabinClass.Premium]: 'CABIN_CLASS_PREMIUM_ECONOMY',
          [FlightCore.CabinClass.First]: 'CABIN_CLASS_FIRST',
        };

        const requestBody: SearchCriteriaRequest = {
          query: {
            cabinClass: cabinClassMap[this.cabinClass],
            adults: this.adults,
            queryLegs,
          },
        };

        const childrenAges =
          this.childrenAges.filter(it => typeof it === 'number') ?? [];

        if (childrenAges.length > 0) {
          requestBody.query['childrenAges'] = childrenAges;
        }

        return requestBody as T;
      } else if (type === RequestTypes.BrowsePrice) {
        const queryLegs: IndicativeQueryLeg[] = [];

        const dateRange = (date: Date) => {
          return {
            startDate: {
              month: date.getMonth() + 1,
              year: date.getFullYear(),
            },
            endDate: {
              month: date.getMonth() + 1,
              year: date.getFullYear(),
            },
          };
        };

        queryLegs.push({
          originPlace: {
            queryPlace: {
              iata: this.flightFromId,
            },
          },
          destinationPlace: {
            queryPlace: {
              iata: this.flightToId,
            },
          },
          dateRange: dateRange(this.departDate),
        });

        if (
          this.tripType === FlightCore.TripType.RoundTrip &&
          this.returnDate
        ) {
          queryLegs.push({
            originPlace: {
              queryPlace: {
                iata: this.flightToId,
              },
            },
            destinationPlace: {
              queryPlace: {
                iata: this.flightFromId,
              },
            },
            dateRange: dateRange(this.returnDate),
          });
        }

        const requestBody: IndicativeSearchRequest = {
          query: {
            queryLegs,
            dateTimeGroupingType: DateTimeGroupingType.ByDate,
          },
        };

        return requestBody as T;
      } else {
        return null;
      }
    }

    same(ref: SearchCriteria) {
      const compareDate = (a: Date, b: Date): boolean => {
        if (!a || !b) {
          return a === b;
        }
        const d1: Date = parse(a);
        d1.setHours(0, 0, 0, 0);
        const d2: Date = parse(b);
        d2.setHours(0, 0, 0, 0);
        return d1.getTime() === d2.getTime();
      };

      return (
        ref.flightToId === this.flightToId &&
        ref.flightFromId === this.flightFromId &&
        compareDate(ref.departDate, this.departDate) &&
        compareDate(ref.returnDate, this.returnDate) &&
        ref.cabinClass === this.cabinClass &&
        ref.adults === this.adults &&
        ref.tripType === this.tripType
      );
    }

    static parseRoute(route: RouteLocation): SearchCriteria {
      const idParser = (id: string[]): string[] => {
        const parts: string[] = [];
        let part: string[] = [];

        const isNumF = (it: string) => /^\d+$/.test(it);

        id.forEach((it, i) => {
          const isNum = isNumF(it);
          if (part.length == 0) {
            part.push(it);
            return;
          }
          const prevIsNum = isNumF(part[0]);
          const last = i === id.length - 1 && part.length > 0;

          if (isNum === prevIsNum) {
            part.push(it);
          } else {
            parts.push(part.join(''));
            part = [];
            part.push(it);
          }

          if (last) {
            parts.push(part.join(''));
            part = [];
          }
        });
        return parts;
      };

      const id = route.params.id as string;
      const cabinClass = EnumUtils.enumFromStringValue(
        FlightCore.CabinClass,
        route.query.c
          ? route.query.c.toString()
          : FlightCore.CabinClass.Economy,
      );

      const idParts = idParser([...id]);

      const parsedDate = (st: string): Date => {
        try {
          const day = [...st[0], st[1]].join('');
          const month = [...st[2], st[3]].join('');
          const now = new Date();
          const parsed = parse(`${now.getFullYear()}-${month}-${day}`);
          if (differenceInCalendarDays(now, parsed) <= 1) {
            return parsed;
          } else {
            return parse(`${now.getFullYear() + 1}-${month}-${day}`);
          }
        } catch (e) {
          console.log(e);
          return null;
        }
      };

      const parseChildrenAges = (): number[] => {
        try {
          const ch = route.query.ch ? route.query.ch.toString() : '';

          if (typeof ch === 'string') {
            return ch
              .split(',')
              .map(age => parseInt(age.trim(), 10))
              .filter(age => !isNaN(age) && age >= 0);
          }

          return [];
        } catch {
          return [];
        }
      };

      const parseAdults = (): number => {
        try {
          const a = route.query.a ? route.query.a.toString() : '1';

          if (typeof a === 'string') {
            const parsed = parseInt(a.trim(), 10);
            return isNaN(parsed) || parsed < 0 ? 0 : parsed;
          }

          return 1;
        } catch {
          return 1;
        }
      };

      return new SearchCriteria({
        flightFromId: idParts[0],
        departDate: parsedDate(idParts[1]),
        flightToId: idParts[2],
        returnDate: idParts.length === 4 ? parsedDate(idParts[3]) : null,
        tripType:
          idParts.length === 4
            ? FlightCore.TripType.RoundTrip
            : FlightCore.TripType.OneWay,
        adults: parseAdults(),
        cabinClass: cabinClass,
        childrenAges: parseChildrenAges(),
      });
    }
  }
}

export interface OriginPlaceId {
  iata?: string;
  entityId?: string;
}

export interface DestinationPlaceId {
  entityId?: string;
  iata?: string;
}

export interface DateModel {
  year: number;
  month: number;
  day: number;
}

export type Place = OriginPlaceId | { queryPlace: OriginPlaceId };

export interface QueryLeg {
  originPlaceId: Place;
  destinationPlaceId: Place;
  date: DateModel;
}

export interface Query {
  queryLegs: QueryLeg[];
  adults: number;
  cabinClass: string;
  market?: string;
  locale?: string;
  currency?: string;
  excludedAgentsIds?: string[];
}

export interface SearchCriteriaRequest {
  query: Query;
}

export type DateModelShort = Omit<DateModel, 'day'>;

export enum DateTimeGroupingType {
  Default = 'DATE_TIME_GROUPING_TYPE_UNSPECIFIED',
  ByDate = 'DATE_TIME_GROUPING_TYPE_BY_DATE',
  ByMonth = 'DATE_TIME_GROUPING_TYPE_BY_MONTH',
}
export type IndicativeQueryLeg = Omit<
  QueryLeg,
  'date' | 'originPlaceId' | 'destinationPlaceId'
> & {
  anytime?: boolean;
  fixedDate?: DateModel;
  originPlace: Place;
  destinationPlace: Place;
  dateRange?: {
    startDate: DateModelShort;
    endDate: DateModelShort;
  };
};

export type IndicativeQuery = Omit<
  Query,
  'adults' | 'cabinClass' | 'queryLegs'
> & {
  queryLegs: IndicativeQueryLeg[];
  dateTimeGroupingType?: DateTimeGroupingType;
};

export interface IndicativeSearchRequest {
  query: IndicativeQuery;
}
